import type { JSX, PropsWithChildren } from 'react';
import React from 'react';

import { ApolloClient, ApolloLink, InMemoryCache, from } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import { HttpLink } from '@apollo/client/link/http';
import { ApolloProvider } from '@apollo/client/react';
import { getMainDefinition } from '@apollo/client/utilities';
import log from 'loglevel';
import { Observable } from 'rxjs';

import { setupCachePersistence } from '../../apollo/cachePersistence';
import { emailsByOrganizationFieldPolicy } from '../../apollo/emailListCache';
import { useAuth } from '../../hooks/useAuth';
import { createErrorLink } from './errorLink';

const graphqlEndpoint = `${import.meta.env.VITE_API_ENDPOINT}/graphql`;
log.info('🌐 GraphQL Endpoint:', graphqlEndpoint);

const httpLink = new HttpLink({
  uri: graphqlEndpoint,
});

const cache = new InMemoryCache({
  typePolicies: {
    // Has no id, so without this each write replaces the cached object whole.
    // The Slack health banner polls two fields of it; merging keeps the
    // settings page's fuller copy intact instead of forcing it to refetch.
    SlackWorkspaceStatus: { merge: true },
    // Same reason: the sidebar reads only `activated`, and replacing the
    // Modules screen's full status with that would force it to refetch.
    OkrModuleStatusType: { merge: true },
    Query: {
      fields: {
        // Cursor-paged Conversation list: merge pages instead of replacing them.
        emailsByOrganization: emailsByOrganizationFieldPolicy,
        incubators: {
          merge(existing, incoming, { args }) {
            if (args?.cursor) {
              const results = existing ? [...existing.results] : [];
              incoming.results.forEach((incubator: unknown) => {
                results.push(incubator);
              });

              return {
                ...incoming,
                next: incoming.next,
                results,
              };
            }

            return {
              ...incoming,
              next: incoming.next,
              results: incoming.results,
            };
          },

          read(existing) {
            if (existing) {
              return {
                ...existing,
                next: existing.next,
                results: Object.values(existing.results),
              };
            }
            return undefined;
          },
        },
      },
    },
  },
});

// Offline write guard — while the browser reports no connectivity, fail
// mutations fast with a clear message rather than letting them hang or
// silently drop. Reads still fall through to the (persisted) cache.
const offlineGuardLink = new ApolloLink((operation, forward) => {
  const definition = getMainDefinition(operation.query);
  const isMutation =
    definition.kind === 'OperationDefinition' && definition.operation === 'mutation';

  if (isMutation && typeof navigator !== 'undefined' && navigator.onLine === false) {
    return new Observable((observer) => {
      observer.error(new Error("You're offline. This change can't be saved until you reconnect."));
    });
  }

  return forward(operation);
});

export default function AuthorizedApolloProvider({ children }: PropsWithChildren): JSX.Element {
  const { getAccessTokenSilently, logout, user } = useAuth();

  // Gate the first render on restoring the persisted cache so offline data is
  // available immediately. Resolves near-instantly when unauthenticated (no
  // scope key) or when there is nothing persisted yet.
  //
  // Keyed on the LIVE authenticated user id (not the stored lastLoggedInUserId,
  // which can lag behind an identity change) so a different user re-logging in
  // on the same browser can never restore the previous user's cache. Re-runs
  // when the authenticated identity resolves or changes.
  const userId = user?.sub;
  const activeTenantId =
    typeof window !== 'undefined'
      ? localStorage.getItem('selectedTenantId') || sessionStorage.getItem('selectedTenantId')
      : null;
  // The scope we must have restored before rendering. '__anonymous__' covers
  // login screens (no user/tenant → nothing persisted).
  const scopeKey = userId && activeTenantId ? `${userId}::${activeTenantId}` : '__anonymous__';

  // Track WHICH scope has been restored — not a monotonic boolean. When the
  // identity or tenant changes, readiness is invalidated and children are
  // withheld until the cache is re-scoped, so a new session can never render
  // against the previous scope's cache.
  const [readyScope, setReadyScope] = React.useState<string | null>(null);
  React.useEffect(() => {
    let cancelled = false;
    setReadyScope(null);
    setupCachePersistence(cache, userId, activeTenantId).finally(() => {
      if (!cancelled) {
        setReadyScope(scopeKey);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [scopeKey, userId, activeTenantId]);

  // Auth link - adds authorization header (when available)
  const authLink = setContext(async (operation, { headers }) => {
    // Check if this is a public operation that doesn't require auth
    const firstDefinition = operation.query.definitions[0];
    const operationName =
      firstDefinition && 'name' in firstDefinition && firstDefinition.name
        ? firstDefinition.name.value
        : operation.operationName || 'Unknown';

    // List of operations that should work without authentication
    const publicOperations = [
      'invitationByToken',
      'GetInvitationByToken',
      'MemberInviteByToken',
    ];
    const isPublicOperation = publicOperations.some((op) =>
      operationName.toLowerCase().includes(op.toLowerCase()),
    );

    try {
      const token = await getAccessTokenSilently();

      const authHeaders = {
        ...headers,
        // Ensure requests trigger a CORS preflight to satisfy Apollo Server CSRF prevention
        'apollo-require-preflight': 'true',
        authorization: token ? `Bearer ${token}` : '',
      };

      return { headers: authHeaders };
    } catch (error) {
      // For public operations, allow the request to proceed without auth
      if (isPublicOperation) {
        log.debug(`Public operation ${operationName} proceeding without auth`);
        return {
          headers: {
            ...headers,
            'apollo-require-preflight': 'true',
          },
        };
      }

      // For authenticated operations, if we can't get a token, clear everything and force re-auth
      log.error('Failed to get access token:', error);
      localStorage.clear();
      sessionStorage.clear();
      logout({ returnTo: window.location.origin });
      throw error;
    }
  });

  // Tenant link - adds X-Tenant-ID header with robust fallback logic
  const tenantLink = setContext((operation, { headers }) => {
    // Always preserve existing headers
    const newHeaders = { ...headers };

    // Get operation name from the operation definition
    const firstDefinition = operation.query.definitions[0];
    const definitionName =
      firstDefinition && 'name' in firstDefinition && firstDefinition.name
        ? firstDefinition.name.value
        : 'Unknown';
    const operationName = operation.operationName || definitionName;

    // Don't send tenant header for tenant management and auth operations
    // These operations either manage tenant selection or work without tenant context
    const tenantManagementOps = [
      'GetMyTenants',
      'SelectTenant',
      'GetInvitationByToken',
      'invitationByToken',
      'AcceptInvitation',
      // The Slack join page: the person has no company yet, and a stale
      // X-Tenant-ID from a previous login would be refused as a tenant they do
      // not belong to.
      'MemberInviteByToken',
      'AcceptMemberInvite',
      'CreateTenant',
    ];
    const isTenantOp = tenantManagementOps.some(
      (op) => operationName.toLowerCase() === op.toLowerCase(),
    );

    if (!isTenantOp) {
      // Try multiple sources for tenant ID with priority order
      const tenantId =
        headers['X-Tenant-ID'] || // From retry context
        localStorage.getItem('selectedTenantId') || // From storage
        sessionStorage.getItem('selectedTenantId'); // Fallback to session storage

      if (tenantId) {
        newHeaders['X-Tenant-ID'] = tenantId;

        // Also store in sessionStorage as backup
        if (!sessionStorage.getItem('selectedTenantId')) {
          sessionStorage.setItem('selectedTenantId', tenantId);
        }
      }
    }

    return { headers: newHeaders };
  });

  const errorLink = createErrorLink({ getAccessTokenSilently, logout });

  const client = React.useRef(
    new ApolloClient({
      link: from([offlineGuardLink, errorLink, authLink, tenantLink, httpLink]),
      cache,
      devtools: { enabled: true },
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'cache-and-network',
        },
      },
    }),
  );

  // Withhold render until the cache restored for the CURRENT scope. Using a
  // scope match (not a one-way flag) means an identity/tenant change re-gates
  // rendering until the new scope is restored, so stale-scope data can't paint.
  if (readyScope !== scopeKey) {
    return <></>;
  }

  return <ApolloProvider client={client.current} children={children} />;
}
